import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckCircle,
  Close,
  CloudUpload,
  Download,
  ErrorOutline,
  WarningAmber,
} from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import { useCallback, useEffect, useRef, useState } from 'react';
import { parseCsv, downloadCsvTemplate } from '../../utils/csvParser';
import {
  type CsvRowValidation,
  type FileValidationError,
  type NormalizedSampleRow,
  LOCATION_ID_HEADER,
  applyHeaderAliases,
  checkInFileDuplicates,
  validateFile,
  validateHeaders,
  validateRow,
  validateRowCount,
} from './csvUploadValidation';
import { resolveLocationTarget, summarizeLocationTargets } from './sampleCsvImport';
import type { Location } from '../../types/location';

export type ImportDepthUnit = 'm' | 'ft';

interface CsvUploadModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * BUG-2026-0170 follow-up (Gigi 07-31): the file's unit system is ASKED,
   * never assumed — values import verbatim and the chosen unit becomes the
   * project's depth-unit designation.
   */
  onImport: (samples: NormalizedSampleRow[], unit: ImportDepthUnit) => Promise<void>;
  /** The project's current designation — preselects the question. */
  defaultUnit?: ImportDepthUnit;
  /** True when the project already holds samples (mixing-units warning). */
  hasExistingSamples?: boolean;
  /**
   * FEAT-2026-0017: the side-panel selection — where rows with a blank
   * "Location ID" go — and every location in the project, so the preview can
   * say which ids are new (and will be created on import).
   */
  selectedLocationId: string;
  locations: Location[];
}

export const CsvUploadModal = ({
  open, onClose, onImport, defaultUnit = 'm', hasExistingSamples = false,
  selectedLocationId, locations,
}: CsvUploadModalProps) => {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileError, setFileError] = useState<FileValidationError | null>(null);
  const [validations, setValidations] = useState<CsvRowValidation[]>([]);
  const [fileName, setFileName] = useState('');
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState('');
  const [unit, setUnit] = useState<ImportDepthUnit>(defaultUnit);

  // Re-arm the unit question with the project's designation on every open.
  useEffect(() => {
    if (open) setUnit(defaultUnit);
  }, [open, defaultUnit]);

  const validCount = validations.filter((v) => v.valid).length;
  const errorCount = validations.filter((v) => !v.valid).length;
  const warningCount = validations.filter((v) => v.valid && v.errors.some((e) => e.severity === 'warning')).length;
  const hasData = validations.length > 0;
  // FEAT-2026-0017: which locations the valid rows land in, and which are new.
  const targets = summarizeLocationTargets(
    validations.filter((v) => v.valid && v.normalized).map((v) => v.normalized!),
    selectedLocationId,
    locations,
  );

  const reset = () => {
    setFileError(null);
    setValidations([]);
    setFileName('');
    setImportError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => { reset(); onClose(); };

  const handleFileSelect = useCallback((file: File) => {
    setImportError('');
    const fileLevelErr = validateFile(file);
    if (fileLevelErr) { setFileError(fileLevelErr); setValidations([]); return; }

    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const { headers, rows, rawRowNumbers } = parseCsv(text);

      const headerErr = validateHeaders(headers);
      if (headerErr) { setFileError(headerErr); setValidations([]); return; }

      const rowCountErr = validateRowCount(rows.length);
      if (rowCountErr) { setFileError(rowCountErr); setValidations([]); return; }

      setFileError(null);
      applyHeaderAliases(rows);
      const results = rows.map((row, i) => validateRow(row, rawRowNumbers[i]));
      checkInFileDuplicates(results);
      setValidations(results);
    };
    reader.readAsText(file);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  }, [handleFileSelect]);

  const handleImport = async () => {
    const validSamples = validations
      .filter((v) => v.valid && v.normalized)
      .map((v) => v.normalized!);
    if (validSamples.length === 0) return;

    setImporting(true);
    setImportError('');
    try {
      await onImport(validSamples, unit);
      handleClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setImportError(msg || 'Upload failed. Please check your connection and try again.');
    } finally {
      setImporting(false);
    }
  };

  // Lookup keys stay the template's header names; only the LABEL follows the
  // answered unit so the preview reads in the file's own system.
  const previewCols: { key: string; label: string }[] = [
    { key: LOCATION_ID_HEADER, label: 'Location ID' },
    { key: 'Sample Reference', label: 'Sample Reference' },
    { key: 'Depth Top (m)', label: `Depth Top (${unit})` },
    { key: 'Depth Base (m)', label: `Depth Base (${unit})` },
    { key: 'Type', label: 'Type' },
    { key: 'Sample Status', label: 'Sample Status' },
    { key: 'Priority', label: 'Priority' },
    { key: 'Condition', label: 'Condition' },
    { key: 'Comments', label: 'Comments' },
    { key: 'Shipment Box/Bucket', label: 'Shipment Box/Bucket' },
    { key: 'Storage Location', label: 'Storage Location' },
  ];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 700 }}>
        Upload Samples
        <IconButton size="small" onClick={handleClose}><Close /></IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        {/* Template download */}
        <Button
          startIcon={<Download />}
          onClick={downloadCsvTemplate}
          size="small"
          sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 600 }}
        >
          Download CSV Template
        </Button>

        {/* FEAT-2026-0017: one file, many boreholes — via the optional Location ID column. */}
        <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
          Optional <b>Location ID</b> column: put each row&rsquo;s borehole / test pit id there to
          load several locations from one file. Rows with a blank Location ID go to
          <b> {selectedLocationId}</b> (the selected location). Ids the project doesn&rsquo;t have yet
          are created automatically as Boreholes named after the id (editable afterwards).
        </Typography>

        {/* THE unit question (Gigi 07-31): asked every import, never assumed. */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
          borderRadius: 2, px: 2, py: 1.25,
          backgroundColor: alpha(theme.palette.primary.main, 0.04),
        }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Depths in this file are in:
          </Typography>
          <ToggleButtonGroup
            exclusive size="small" value={unit}
            onChange={(_e, v) => v && setUnit(v as ImportDepthUnit)}
          >
            <ToggleButton value="m" sx={{ textTransform: 'none', px: 2 }}>Metres (m)</ToggleButton>
            <ToggleButton value="ft" sx={{ textTransform: 'none', px: 2 }}>Feet (ft)</ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 220 }}>
            Values are imported exactly as they appear — no conversion. Your answer becomes the
            project's depth unit (grids and reports label depths accordingly).
          </Typography>
        </Box>
        {hasExistingSamples && unit !== defaultUnit && (
          <Alert severity="warning" sx={{ py: 0.5 }}>
            This project already holds samples designated in <strong>{defaultUnit === 'm' ? 'metres' : 'feet'}</strong>.
            Importing this file as <strong>{unit === 'm' ? 'metres' : 'feet'}</strong> switches the whole
            project's designation — existing depth values are NOT converted, so make sure everything
            in this project really is in one unit system.
          </Alert>
        )}

        {/* Drop zone */}
        <Box
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          sx={{
            border: `2px dashed ${alpha(theme.palette.primary.main, 0.3)}`,
            borderRadius: 2,
            py: 4,
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            backgroundColor: alpha(theme.palette.primary.main, 0.02),
            '&:hover': {
              borderColor: theme.palette.primary.main,
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
            },
          }}
        >
          <CloudUpload sx={{ fontSize: 40, color: alpha(theme.palette.primary.main, 0.4), mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            {fileName || 'Drag and drop your CSV file here, or click to browse'}
          </Typography>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFileSelect(file);
            }}
          />
        </Box>

        {/* File-level error */}
        {fileError && <Alert severity="error">{fileError.message}</Alert>}

        {/* Import error */}
        {importError && <Alert severity="error">{importError}</Alert>}

        {/* Summary + Preview */}
        {hasData && (
          <>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Chip
                icon={<CheckCircle />}
                label={`${validCount} valid`}
                color="success"
                size="small"
                variant="outlined"
              />
              {warningCount > 0 && (
                <Chip
                  icon={<WarningAmber />}
                  label={`${warningCount} warning${warningCount !== 1 ? 's' : ''}`}
                  color="warning"
                  size="small"
                  variant="outlined"
                />
              )}
              {errorCount > 0 && (
                <Chip
                  icon={<ErrorOutline />}
                  label={`${errorCount} error${errorCount !== 1 ? 's' : ''}`}
                  color="error"
                  size="small"
                  variant="outlined"
                />
              )}
              <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                {validCount} of {validations.length} rows ready to import
                {targets.locationIds.length > 1 && ` · ${targets.locationIds.length} locations`}
              </Typography>
            </Box>

            {/* FEAT-2026-0017: say up front which locations will be created. */}
            {targets.newLocationIds.length > 0 && (
              <Alert severity="info" sx={{ py: 0.5 }}>
                {targets.newLocationIds.length} new location{targets.newLocationIds.length !== 1 ? 's' : ''} will
                be created as <strong>Borehole</strong> (name = id): <strong>{targets.newLocationIds.join(', ')}</strong>.
                Change the type or name afterwards from the Locations panel if needed.
              </Alert>
            )}

            <TableContainer sx={{ maxHeight: 300, border: `1px solid ${alpha(theme.border.color, 0.5)}`, borderRadius: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ '& th': { backgroundColor: theme.table.headerBg, fontWeight: 600, fontSize: '0.75rem' } }}>
                    <TableCell sx={{ width: 36 }}>#</TableCell>
                    {previewCols.map((col) => <TableCell key={col.key}>{col.label}</TableCell>)}
                    <TableCell sx={{ width: 36 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {validations.map((v, i) => (
                    <TableRow
                      key={i}
                      sx={{
                        backgroundColor: !v.valid
                          ? alpha(theme.palette.error.main, 0.05)
                          : v.errors.length > 0
                            ? alpha(theme.palette.warning.main, 0.06)
                            : undefined,
                      }}
                    >
                      <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{v.rowNumber}</TableCell>
                      {previewCols.map((col) => {
                        const hasWarning = v.errors.some((e) => e.severity === 'warning' && e.field === col.key);
                        // Location ID shows the RESOLVED target: a blank cell reads as the
                        // selected location, dimmed so the fallback is visible at a glance.
                        const isLocationCol = col.key === LOCATION_ID_HEADER;
                        const fellBack = isLocationCol && v.normalized != null && !v.normalized.LocationId;
                        const cellText = isLocationCol && v.normalized
                          ? resolveLocationTarget(v.normalized, selectedLocationId)
                          : (v.row[col.key] ?? '');
                        return (
                          <TableCell
                            key={col.key}
                            sx={{
                              fontSize: '0.8rem',
                              ...(hasWarning && { color: 'warning.dark', fontWeight: 600 }),
                              ...(fellBack && { color: 'text.disabled', fontStyle: 'italic' }),
                            }}
                          >
                            {cellText}
                          </TableCell>
                        );
                      })}
                      <TableCell>
                        {!v.valid ? (
                          <Tooltip title={v.errors.filter((e) => e.severity === 'error').map((e) => e.message).join('\n')}>
                            <ErrorOutline sx={{ fontSize: 16, color: 'error.main' }} />
                          </Tooltip>
                        ) : v.errors.length > 0 ? (
                          <Tooltip title={v.errors.map((e) => e.message).join('\n')}>
                            <WarningAmber sx={{ fontSize: 16, color: 'warning.main' }} />
                          </Tooltip>
                        ) : (
                          <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button
          variant="contained"
          disabled={validCount === 0 || importing}
          onClick={handleImport}
          sx={{ textTransform: 'none', fontWeight: 600, px: 3, borderRadius: 1.5 }}
        >
          {importing ? 'Importing...' : `Import Valid (${validCount})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
